<template>
  <div
    class="date-range-picker"
    :class="{
      'text-right': align === 'right'
    }"
  >
    <v-menu v-model="showMenu" :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <div v-bind="props" :class="rangeStringClass">{{ rangeString }}<v-icon>mdi-chevron-down</v-icon></div>
      </template>
      <v-card id="main-card" class="d-flex">
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col>
              <MinimalDatePicker v-model="vuetifyRange" multiple="range" :key="pickerKey"></MinimalDatePicker>
              <div class="pa-2">
                <v-btn color="primary" variant="text" @click="showMenu = false">Cancel</v-btn>
                <v-btn color="primary" variant="flat" @click="updateRangeFromPicker">Update</v-btn>
              </div>
            </v-col>
            <v-col class="options">
              <div class="pa-4">
                <div class="text-h6">Presets</div>
                <v-btn
                  class="text-body-2 text-capitalize text-left"
                  v-for="(preset, key) in presets"
                  color="primary"
                  variant="text"
                  block
                  :key="key"
                  @click="setPreset(preset.value)"
                  >{{ preset.text }}</v-btn
                >
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-menu>
  </div>
</template>
<script>
import MinimalDatePicker from '@/components/ui/MinimalDatePicker'

export default {
  name: 'DateRangePicker',
  components: { MinimalDatePicker },
  props: ['align', 'default', 'short'],
  data() {
    return {
      showMenu: false,
      range: this.getInitialRange(),
      pickerDisplayDate: null,
      vuetifyRange: [],
      showError: false,
      shortPresets: [
        { text: 'All Time', value: 6 },
        { text: 'This Month', value: 2 },
        { text: 'This Quarter', value: 3 },
        { text: 'Last Month', value: 4 },
        { text: 'Last Quarter', value: 5 }
      ],
      longPresets: [
        { text: 'All Time', value: 6 },
        { text: 'Last 6 months', value: 0 },
        { text: 'Last 12 months', value: 1 },
        { text: 'This Month', value: 2 },
        { text: 'This Quarter', value: 3 },
        { text: 'Last Month', value: 4 },
        { text: 'Last Quarter', value: 5 }
      ]
    }
  },
  watch: {
    range: {
      handler() {
        if (this.range) {
          this.vuetifyRange = this.daysInRange(this.range[0], this.range[1])
          this.pickerDisplayDate = this.range[0].format('YYYY-MM-DD')
          this.$emit('change', this.range)
        }
      },
      immediate: true
    }
  },
  computed: {
    presets() {
      return this.short ? this.shortPresets : this.longPresets
    },
    pickerKey() {
      return this.vuetifyRange.length ? this.$moment(this.vuetifyRange[0]).format('YYYY-MM-DD') : 'empty'
    },
    rangeString() {
      if (this.showError) {
        return 'Error in date range'
      }
      return this.range
        ? this.range[0].format('MMM DD, YYYY') + ' - ' + this.range[1].format('MMM DD, YYYY')
        : 'Select a date range'
    },
    rangeStringClass() {
      return {
        'error-text': this.showError
      }
    }
  },
  methods: {
    getInitialRange() {
      // Handle default prop to set initial range
      switch (this.default) {
        case 'empty':
          return null
        case 'allTime':
          return this.allTime()
        case 'lastSixMonths':
          return this.lastSixMonths()
        case 'lastTwelveMonths':
          return this.lastTwelveMonths()
        case 'thisMonth':
          return this.thisMonth()
        default:
          // If no default prop specified, use thisMonth as fallback
          return this.thisMonth()
      }
    },
    updateRangeFromPicker() {
      const picked = (this.vuetifyRange || []).filter(Boolean)
      if (!picked.length) {
        this.showErrorState()
        return
      }

      const startDate = this.$moment(picked[0]).startOf('day')
      const endDate = this.$moment(picked[picked.length - 1]).startOf('day')

      // Validate dates are valid
      if (!startDate.isValid() || !endDate.isValid()) {
        this.showErrorState()
        return
      }

      // Check if start date is before or equal to end date
      if (startDate.isAfter(endDate)) {
        this.showErrorState()
        return
      }

      // Range is valid
      this.range = [startDate, endDate]
      this.showError = false
      this.showMenu = false
    },
    daysInRange(start, end) {
      const day = this.$moment(start).startOf('day')
      const last = this.$moment(end).startOf('day')
      const days = []
      while (!day.isAfter(last)) {
        days.push(day.format('YYYY-MM-DD'))
        day.add(1, 'day')
      }
      return days
    },
    showErrorState() {
      this.showError = true
      this.showMenu = false
      // Clear error message after 2 seconds
      setTimeout(() => {
        this.showError = false
      }, 2000)
    },
    setPreset(num) {
      this.showMenu = false
      switch (num) {
        case 0:
          this.range = this.lastSixMonths()
          break
        case 1:
          this.range = this.lastTwelveMonths()
          break
        case 2:
          this.range = this.thisMonth()
          break
        case 3:
          this.range = this.thisQuarter()
          break
        case 4:
          this.range = this.lastMonth()
          break
        case 5:
          this.range = this.lastQuarter()
          break
        case 6:
          this.range = this.allTime()
          break
        default:
          break
      }
    },
    lastSixMonths() {
      return [this.$moment().subtract(6, 'months').startOf('day'), this.$moment().startOf('day')]
    },
    lastTwelveMonths() {
      return [this.$moment().subtract(12, 'months').startOf('day'), this.$moment().startOf('day')]
    },
    last30() {
      return [this.$moment().subtract(30, 'days').startOf('day'), this.$moment().startOf('day')]
    },
    last90() {
      return [this.$moment().subtract(90, 'days').startOf('day'), this.$moment().startOf('day')]
    },
    thisMonth() {
      return [this.$moment().startOf('month'), this.$moment().startOf('day')]
    },
    thisQuarter() {
      return [this.$moment().startOf('quarter'), this.$moment().endOf('quarter')]
    },
    lastMonth() {
      return [this.$moment().subtract(1, 'month').startOf('month'), this.$moment().subtract(1, 'month').endOf('month')]
    },
    lastQuarter() {
      return [
        this.$moment().subtract(1, 'quarter').startOf('quarter'),
        this.$moment().subtract(1, 'quarter').endOf('quarter')
      ]
    },
    allTime() {
      return [this.$moment('2019-01-01').startOf('day'), this.$moment().startOf('day')]
    }
  }
}
</script>
<style lang="scss" scoped>
.date-range-picker {
  width: 500px !important;
}
.options {
  border-left: solid 1px black;
}
.error-text {
  color: #f44336 !important;
}
</style>
